import { useState, useCallback, useEffect, useMemo } from "react";
import { fetchAllProductDetails } from "@campgladiator/cg-common.api.products";
import { Option } from "@campgladiator/cgui-core.atoms.dropdown";
import {
  ProductDetailDTO,
  ProductDTO,
  ProgramScheduleDTO,
  Role,
  SubscriptionDTO,
} from "@campgladiator/cg-common.types.types";
import { ButtonProps } from "@campgladiator/cgui-core.atoms.button";
import { CreateMembershipModalProps } from "../components/CreateMembershipModal";
import { CreateMembershipSelectProductProps } from "../components/CreateMembershipModal/components/CreateMembershipSelectProduct";
import { fetchSubscriptionsByUser } from "@campgladiator/cg-common.api.subscriptions";
import { getUpgradeableProducts } from "@campgladiator/cg-common.logic.subscriptions";
import { parseMemberships } from "@campgladiator/cg-common.logic.memberships";
import { SelectTrainerInputProps } from "../components/SelectTrainerInput";
import { getProgramApprovedTypeBySelectedProductDetail } from "../components/SelectTrainerInput/logic";
import { SelectedTrainer } from "../components/SelectTrainerInput/SelectTrainerInput";
import { militaryTimeToMeridianTime } from "@campgladiator/cg-common.utility.dates";
import useSelectCouponInput from "../components/SelectCouponInput/useSelectCouponInput";

export type UseMembershipManagementCreateProps = {
  onClose?: (success?: boolean) => void;
  userId: string;
  role: Role;
  token: string;
  stripeCustomerId: string;
  sellingTrainerId: string;
  theme: ButtonProps["theme"];
};

const useMembershipManagementCreate = ({
  onClose,
  userId,
  stripeCustomerId,
  sellingTrainerId,
  role,
  token,
  theme,
}: UseMembershipManagementCreateProps) => {
  const [productDetails, setProductDetails] = useState<ProductDetailDTO[]>();
  const [currentUserSubscriptions, setCurrentUserSubscriptions] =
    useState<SubscriptionDTO[]>();
  const [productOptions, setProductOptions] = useState<Option[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(false);

  const loadInitialData = useCallback(async () => {
    try {
      setIsInitialLoading(true);
      const productDetails = await fetchAllProductDetails({});
      const { content: subscriptions } = await fetchSubscriptionsByUser({
        userId,
      });
      const { upgradeableProducts } = await getUpgradeableProducts({
        productDetails,
        role,
        userSubscriptions: subscriptions,
      });
      setProductOptions(parseMemberships(upgradeableProducts));
      setProductDetails(productDetails);
      setCurrentUserSubscriptions(subscriptions);
    } catch (error) {
      console.error("Error loading initial data", error);
    } finally {
      setIsInitialLoading(false);
    }
  }, [userId]);

  const membershipManagementCreateModalProps: CreateMembershipModalProps = {
    onClose,
    theme,
    stripeCustomerId,
    sellingTrainerId,
    userId,
    isInitialLoading,
    productDetails,
    productOptions,
    role,
  };

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  return {
    membershipManagementCreateModalProps,
  };
};

export default useMembershipManagementCreate;
